import React, { useState, useCallback } from 'react'
import { Toast } from 'react-vant'
import { APIWeb } from '@/api'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useTranslation } from 'react-i18next'
import { useRechargeListStatus } from '@/hooks/listStatus'
import RefreshList from '@/components/RefreshList'

export default React.memo(function DepositRecords() {
  const { t } = useTranslation()
  const { getRechargeListStatus } = useRechargeListStatus()
  const [data, setData] = useState<ResponsesTransactionListItem[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const fetchData = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const response = await APIWeb.getDepositRecords({
        page,
        page_size: pageSize
      })
      if (response?.list) {
        const newData = response.list
        
        if (isAppend) {
          setData(prevData => [...prevData, ...newData])
        } else {
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2)
          } else {
            setCurrentPage(prev => prev + 1)
          }
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取充币记录失败:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [loading])

  const loadMore = useCallback(async () => {
    await fetchData(currentPage, true)
  }, [fetchData, currentPage])

  const onRefresh = useCallback(async () => {
    await fetchData(1, false)
  }, [fetchData])

  const renderItem = (item: ResponsesTransactionListItem, index: number) => (
    <div
      key={`${item.token_id}-${index}`}
      className={`pb-4 border-[0.5px] border-b-solid border-[#CCCCCC] ${index === data.length - 1 ? '' : 'border-b border-gray-300'}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-3.75 text-black font-bold">
          {item.amount} {item.symbol}
        </span>
        <span className="text-2.5 text-black/36 font-bold">
          {item.created_at_string}
        </span>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-2.5 text-[#1A1A1A]">
          {t('X个区块确认', { confirmed: item.confirmed_num, threshold: item.confirming_threshold })}
        </span>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-[#1A1A1A]">
          {item.chain}
        </span>
        <span className={`text-xs text-black`}>
          {getRechargeListStatus(item.status || '')}
        </span>
      </div>
    </div>
  )

  return (
    <PageLayout>
      <Header leftChildren={<Back />} centerChildren={<div className="text-3.75 font-medium text-black">{t('充币记录')}</div>} />
      <div className="px-6 pt-4 flex flex-col" style={{ minHeight: 'calc(100vh - 3.5rem)' }}>
        <div className="text-3 text-black">{t('全部充币记录')}</div>
        <div className="mt-4 flex flex-col flex-1">
          <RefreshList
            data={data}
            renderItem={renderItem}
            keyExtractor={(item, index) => `${item.token_id}-${index}`}
            loading={loading}
            hasMore={hasMore}
            onRefresh={onRefresh}
            onLoadMore={loadMore}
            emptyText={t('暂无充币记录')}
            finishedText={t('没有更多数据了')}
            loadingText={t('加载中')}
            itemClassName="space-y-4"
            className="flex-1"
          />
        </div>
      </div>
    </PageLayout >
  )
})